import { waitForBalanceSDK } from '@b-stock/fe-core/pay-ui'

import {
  cardAddRequest,
  completeCardAdd,
  type CardAddOperation,
} from './balanceCardAddApi'

/**
 * Resolve the payment method ID from the Balance SDK's onSuccess argument.
 *
 * The live SDK (`sdk-latest.js`) calls onSuccess with an object,
 * `{ paymentMethodId, paymentMethodType }`. The bare-string form is still
 * accepted for the older SDK contract. Anything else (including a blank ID)
 * resolves to `undefined`.
 */
export const getBalancePaymentMethodId = (
  result: unknown
): string | undefined => {
  const id =
    typeof result === 'object' && result !== null && 'paymentMethodId' in result
      ? result.paymentMethodId
      : result
  return typeof id === 'string' && id.trim() ? id : undefined
}

// Balance exposes a single widget per window. A new attempt invalidates old callbacks.
let widgetGeneration = 0

type InitializeBalanceWidgetParams = {
  buyerAccountId: string
  accessToken: string
  getAccessToken?: () => string
  onSuccess: (paymentMethodId: string) => void | Promise<void>
  onError: (error?: unknown) => void
  onClose?: () => void
  onCancel?: () => void
  isCurrent?: () => boolean
}

export const initializeBalanceWidget = async ({
  buyerAccountId,
  accessToken,
  getAccessToken = () => accessToken,
  onSuccess,
  onError,
  onClose,
  onCancel,
  isCurrent = () => true,
}: InitializeBalanceWidgetParams): Promise<void> => {
  const generation = ++widgetGeneration
  const controller = new AbortController()
  let canceled = false
  let completing = false
  let succeeded = false
  const current = () =>
    !canceled && generation === widgetGeneration && isCurrent()
  const cancel = (callback?: () => void) => {
    if (!current()) return
    callback?.()
    if (completing || succeeded) return
    canceled = true
    controller.abort()
  }
  try {
    if (!buyerAccountId || !getAccessToken())
      throw new Error('Authenticated account is required')
    await waitForBalanceSDK()
    if (!current()) return
    // Never reuse a token/operation from React Query's seven-day cache.
    const operation = await cardAddRequest<CardAddOperation | null>(
      buyerAccountId,
      getAccessToken(),
      'card-add-operations',
      undefined,
      controller.signal
    )
    if (!current()) return
    if (
      !operation ||
      typeof operation.token !== 'string' ||
      !operation.token ||
      typeof operation.operationId !== 'string' ||
      !operation.operationId ||
      typeof operation.expiresAt !== 'string' ||
      !(Date.parse(operation.expiresAt) > Date.now())
    ) {
      throw new Error('Invalid card add operation')
    }
    const paymentMethod = window.balanceSDK?.paymentMethod
    if (!paymentMethod)
      throw new Error('Balance SDK paymentMethod not available')
    paymentMethod.init({
      paymentMethodToken: operation.token,
      allowedPaymentMethods: ['creditCard'],
      // The SDK passes { paymentMethodId, paymentMethodType }; see
      // getBalancePaymentMethodId for the accepted shapes.
      onSuccess: (result: unknown) => {
        if (!current() || completing || succeeded) return
        completing = true
        void (async () => {
          try {
            const paymentMethodId = getBalancePaymentMethodId(result)
            if (!paymentMethodId)
              throw new Error('Balance callback is missing its method ID')
            if (!(Date.parse(operation.expiresAt) > Date.now()))
              throw new Error('Card add operation expired')
            await completeCardAdd(
              buyerAccountId,
              getAccessToken,
              operation.operationId,
              paymentMethodId,
              controller.signal,
              current
            )
            if (!current()) return
            succeeded = true
            await onSuccess(paymentMethodId)
          } catch (error) {
            // A UI handler failure cannot undo a completed provider association.
            if (current() && !succeeded) onError(error)
          } finally {
            completing = false
          }
        })()
      },
      onError: (error?: unknown) => {
        if (current()) onError(error)
      },
      // Closing the widget is a UI event, not proof that the add was canceled.
      // The exact success callback can arrive before or after it.
      onClose: () => {
        if (current()) onClose?.()
      },
      onCancel: () => cancel(onCancel),
    })
    paymentMethod.render(operation.token, '#balance-sdk')
  } catch (error) {
    if (current()) onError(error)
  }
}
